import { Chip, MenuItem, Theme } from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { InternalUser } from './models/InternalUser';
import {
  getTodaysDate,
  ONELIFE_URL,
  SCHEDULE_EDITOR_LEGACY_PATH,
  SCHEDULE_EDITOR_PATH,
} from './utils/constants';
import { RoleName, useProfile } from './hooks/useProfile';

interface SchedulingMenuItemsProps {
  profile: InternalUser;
  handleClose: () => void;
}

type MenuGroup = {
  id: string;
  items: SchedulingMenuItemConfig[];
  heading?: string;
};

type SchedulingMenuItemConfig = {
  label: string;
  to?: string;
  href?: string;
  dataCy: string;
  showNewChip?: boolean;
  requiresRole?: RoleName;
};

const newChipStyles = (theme: Theme) => ({
  ml: 1,
  borderRadius: '4px',
  lineHeight: '150%',
  letterSpacing: '0.16px',
  display: 'flex',
  alignItems: 'center',
  padding: '3px 4px',
  '&&& span': {
    fontSize: '12px',
    fontWeight: theme.typography.fontWeightBold,
    padding: '0 6px',
    '&:hover': {
      backgroundColor: 'transparent',
      cursor: 'default',
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
    '&.MuiChip-outlined:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
    '&:focus': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
    '&:active': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
    },
  },
});

const menuGroups = (scheduleEditorPath: string, providerId = '') =>
  [
    {
      id: 'new-scheduling',
      items: [
        {
          label: 'Schedule Editor',
          to: scheduleEditorPath,
          dataCy: 'schedule-editor-nav',
          showNewChip: true,
        },
        {
          label: 'Provider Schedule',
          to: `/schedule/provider-schedule/provider/${providerId}`,
          dataCy: 'provider-schedule-nav',
        },
        {
          label: 'Office Schedule',
          href: `${ONELIFE_URL}/admin/offices/{{officeId}}/schedules?date={{todaysDate}}`,
          dataCy: 'office-schedule-nav',
        },
        {
          label: 'Office Management',
          to: '/schedule/office-management',
          dataCy: 'office-management-nav',
        },
        {
          label: 'Holidays',
          to: '/schedule/holidays',
          dataCy: 'holidays-nav',
        },
        {
          label: 'Schedule Templates',
          to: '/schedule/templates',
          dataCy: 'schedule-templates-nav',
          requiresRole: 'CALENDAR_ADMIN',
        },
      ],
    },
    {
      // Legacy scheduling items
      id: 'legacy-scheduling',
      heading: 'LEGACY SCHEDULING',
      items: [
        {
          label: 'View Schedules',
          href: `${ONELIFE_URL}/admin/offices/{{officeId}}/appointment_inventories`,
          dataCy: 'legacy-scheduling-view-schedules-nav',
        },
        {
          label: 'Publish Schedules',
          href: `${ONELIFE_URL}/admin/publishing`,
          dataCy: 'legacy-scheduling-publish-schedules-nav',
        },
        {
          label: 'Weekly Shifts',
          href: `${ONELIFE_URL}/admin/offices/{{officeId}}/weekly_shifts`,
          dataCy: 'legacy-scheduling-weekly-shifts-nav',
        },
        {
          label: 'Template Editor',
          to: '/schedule/templates',
          dataCy: 'legacy-scheduling-template-editor-nav',
          requiresRole: 'CALENDAR_ADMIN',
        },
      ],
    },
  ] as MenuGroup[];

const officeMenuGroups: MenuGroup[] = [
  {
    id: 'legacy-scheduling',
    heading: 'LEGACY SCHEDULING',
    items: [
      {
        label: 'Office Management',
        to: '/schedule/office-management',
        dataCy: 'office-management-nav',
      },
      {
        label: 'Office Schedule',
        href: `${ONELIFE_URL}/admin/offices/{{officeId}}/schedules?date={{todaysDate}}`,
        dataCy: 'office-schedule-nav',
      },
      {
        label: 'Holidays',
        to: '/schedule/holidays',
        dataCy: 'holidays-nav',
      },
      {
        label: 'Template Editor',
        to: '/schedule/templates',
        dataCy: 'legacy-scheduling-template-editor-nav',
        requiresRole: 'CALENDAR_ADMIN',
      },
      {
        label: 'Weekly Shifts',
        href: `${ONELIFE_URL}/admin/offices/{{officeId}}/weekly_shifts`,
        dataCy: 'legacy-scheduling-weekly-shifts-nav',
      },
      {
        label: 'Publish Schedules',
        href: `${ONELIFE_URL}/admin/publishing`,
        dataCy: 'legacy-scheduling-publish-schedules-nav',
      },
      {
        label: 'View Schedules',
        href: `${ONELIFE_URL}/admin/offices/{{officeId}}/appointment_inventories`,
        dataCy: 'legacy-scheduling-view-schedules-nav',
      },
    ],
  },
];

function SchedulingMenuItems({ profile, handleClose }: SchedulingMenuItemsProps) {
  const { hasRole, id } = useProfile();
  const todaysDate = getTodaysDate();
  const location = useLocation();
  const { officeScheduleNavBar, renameSchedulingPaths } = useFlags();

  const scheduleEditorBasePath = renameSchedulingPaths
    ? SCHEDULE_EDITOR_PATH
    : SCHEDULE_EDITOR_LEGACY_PATH;

  const dispalyMenuGroups: MenuGroup[] = officeScheduleNavBar
    ? officeMenuGroups
    : menuGroups(scheduleEditorBasePath, id);
  return (
    <>
      {dispalyMenuGroups.map((group) => (
        <div key={group.id}>
          {group.heading && (
            <MenuItem
              sx={{
                pointerEvents: 'none',
                typography: 'overline',
                color: 'text.secondary',
                py: 1,
              }}
            >
              {group.heading}
            </MenuItem>
          )}

          {group.items.map(({ label, to, href, dataCy, showNewChip, requiresRole }) => {
            if (requiresRole && !hasRole(requiresRole)) return null;

            const finalHref = href
              ?.replace('{{officeId}}', profile?.homeOffice?.id || '')
              .replace('{{todaysDate}}', todaysDate);

            const alreadyOnOmniSchedule =
              to === scheduleEditorBasePath && location.pathname.startsWith(to);

            return (
              <MenuItem
                key={dataCy}
                component={to ? NavLink : 'a'}
                to={alreadyOnOmniSchedule ? location.pathname : to}
                href={finalHref}
                data-cy={dataCy}
                onClick={handleClose}
              >
                {label}
                {showNewChip && (
                  <Chip
                    label="NEW"
                    variant="outlined"
                    color="primary"
                    size="small"
                    sx={newChipStyles}
                  />
                )}
              </MenuItem>
            );
          })}
        </div>
      ))}
    </>
  );
}

export default SchedulingMenuItems;
